import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Modal, bjToast, Button } from '@components/common';
import { FormEvent, useEffect, useState } from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { Input } from '@components/common/Forms';
import { isApiV1Errors } from '@utils/data-fetching';

// Interface
// ******************************************
export interface SendCvModalProps {
  locale: Locale;
  userSlug?: string;
  senderEmail?: string;
}

/**
 * @description Modal for sending CV via email to employers.
 *
 * - **locale** - Locale - The current app locale.
 * - **userSlug** - string - The user's slug (optional).
 * - **senderEmail** - string - Pre-filled sender email (optional).
 */
export const SendCvModal = NiceModal.create((props: SendCvModalProps) => {
  // Destructure props
  const { locale, senderEmail = '' } = props;

  // Translation
  const { t } = useTranslation('common');

  // Use a hook to manage the modal state
  const modal = useModal();

  // Form handling
  const {
    record,
    checkFormValidation,
    postFormData,
    formStatus,
    isPosting,
  } = useBjForm({
    submitURL: '/v1/send-own-cv',
    withDefaultValues: { senderEmail },
  });

  // State for form submission
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  // State for field-specific errors
  const [fieldErrors, setFieldErrors] = useState<{
    senderEmail?: string;
    companyName?: string;
    contactName?: string;
    companyEmail?: string;
  }>({});

  // Handle form submission response
  useEffect(() => {
    if (!formSubmitted || isPosting) {
      return;
    }

    // Form submitted successfully
    if (formStatus.actionStatus === 'success') {
      bjToast.success(t('cv.sent'));

      // Close modal after 2 seconds
      setTimeout(() => {
        void modal.hide();
      }, 2000);

      setFormSubmitted(false);
    }

    // Form submitted with error - map errors to fields
    if (formStatus.actionStatus === 'error' && isApiV1Errors(formStatus.data)) {
      const errors: typeof fieldErrors = {};

      // Map API errors to field names
      formStatus.data.errors.forEach((error) => {
        const fieldName = error.path as keyof typeof fieldErrors;
        if (fieldName && ['senderEmail', 'companyName', 'contactName', 'companyEmail'].includes(fieldName)) {
          errors[fieldName] = error.message;
        } else {
          // If error doesn't map to a specific field, show as toast
          bjToast.error(error.message);
        }
      });

      setFieldErrors(errors);
      setFormSubmitted(false);
    }
  }, [formStatus, t, formSubmitted, isPosting, modal]);

  // Close modal
  const closeModal = () => {
    void modal.hide();
  };

  // Handle form submission
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear previous errors
    setFieldErrors({});

    const formData = checkFormValidation(e);

    // Validation failed, return early
    if (typeof formData !== 'object') return;

    postFormData('/v1/send-own-cv', formData);
    setFormSubmitted(true);
  };

  // Render component
  // *****************************************************
  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      size="md"
      backdropClickDisabled={isPosting}
    >
      <Modal.Header
        content={t('cv.action.send.modal.title', { language: t(`global.lang.${locale}`) })}
        onClose={closeModal}
      />

      <Modal.Body className="bg-surface-50">
        <form noValidate onSubmit={onSubmit} className="space-y-4 p-6">
          {/* Description text */}
          <p className="text-sm text-ink-medium">
            {t('cv.action.send.form.text')}
          </p>

          {/* To Section */}
          <div className="space-y-4 border-t pt-4">
            <h6 className="font-bold text-ink">
              {t('global.label.to')}
            </h6>

            {/* Company Name */}
            <Input
              type="text"
              placeholder={t('global.label.company')}
              {...record('companyName')}
              required
              customValidityMessages={{
                valueMissing: t('error.message.empty.employer.name'),
              }}
            />
            {fieldErrors.companyName && (
            <p className="mt-1 text-sm text-error">{fieldErrors.companyName}</p>
            )}

            {/* Contact Name */}
            <Input
              type="text"
              placeholder={t('label.global.contact_person')}
              {...record('contactName')}
              required
              customValidityMessages={{
                valueMissing: t('error.message.empty.contact_name'),
              }}
            />
            {fieldErrors.contactName && (
            <p className="mt-1 text-sm text-error">{fieldErrors.contactName}</p>
            )}

            {/* Company Email */}
            <Input
              type="email"
              placeholder={t('label.global.employer_email')}
              {...record('companyEmail')}
              required
              customValidityMessages={{
                valueMissing: t('error.message.empty.email'),
              }}
            />
            {fieldErrors.companyEmail && (
            <p className="mt-1 text-sm text-error">{fieldErrors.companyEmail}</p>
            )}
          </div>

          {/* From Section */}
          <div className="space-y-4 border-t pt-4">
            <h6 className="font-bold text-ink">
              {t('global.label.from')}
            </h6>

            {/* Sender Email */}
            <Input
              type="email"
              placeholder={t('label.global.your_email')}
              {...record('senderEmail')}
              required
              customValidityMessages={{
                valueMissing: t('error.message.empty.email'),
              }}
            />
            {fieldErrors.senderEmail && (
            <p className="mt-1 text-sm text-error">{fieldErrors.senderEmail}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              color="secondary"
              size="md"
              className="min-w-[200px]"
              isLoading={isPosting}
              disabled={isPosting}
            >
              {t('global.action.send')}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
});

// Typed helper for consumers
export const showSendCvModal = (args: SendCvModalProps) => { void NiceModal.show(SendCvModal, args); };
